import { type MouseEvent } from 'react';
import { Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface OpaqueContractDataDetailsProps {
  calldata: string;
  value: string;
}

/**
 * Read-only details for a contract call that the backend could not decode.
 * The exact transaction fields remain visible without implying a function or
 * parameter interpretation.
 */
export function OpaqueContractDataDetails({
  calldata,
  value,
}: OpaqueContractDataDetailsProps) {
  const handleCopyCalldata = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      await navigator.clipboard.writeText(calldata);
    } catch (error) {
      console.error('Failed to copy calldata:', error);
    }
  };

  return (
    <div className="space-y-3" data-testid="opaque-contract-data-details">
      <div className="grid grid-cols-[auto_1fr] items-baseline gap-x-4 gap-y-3">
        <span className="text-sm font-normal text-muted-foreground">Value</span>
        <code className="min-w-0 break-all text-sm font-medium">
          {value} wei
        </code>
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between gap-3">
          <span className="text-sm font-normal text-muted-foreground">
            Calldata
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 px-2 text-xs text-muted-foreground"
            aria-label="Copy calldata"
            title="Copy calldata"
            onClick={handleCopyCalldata}
          >
            <Copy className="size-3.5" />
            Copy
          </Button>
        </div>
        <pre
          aria-label="Raw calldata"
          className="max-h-48 w-full overflow-auto whitespace-pre-wrap break-all rounded-md bg-muted p-3 font-mono text-xs leading-5"
          tabIndex={0}
        >
          {calldata}
        </pre>
      </div>
    </div>
  );
}
